import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { kayshOperator } from '@karacas/kaysh';
import { _logTap } from '@shared/aux_helper_environment';
import { _get, _ms } from '@shared/aux_helper_functions';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PromotionCombo } from '../../../combos-components-v2/models/combosV2.model';
import { CommunicationItem, PostPreviewPromotionalMessageDTO, PromotionalMessagePreviewResponseDTO } from '../models/communication.models';
import { communicationCombosOptions_dummy, itemsDummyData } from './communication_dummy_data';
import { MessageStructuresListService } from 'app/promotionsv2/configurations/messageStructures/list/service/messageStrucuresList.service';
import {
  MessageStructuresListFiltersModel,
  MessageStructuresModel,
} from 'app/promotionsv2/configurations/messageStructures/list/models/messageStructuresList.models';
import { PromotionTypes } from '@prisma/components/generic-select-promotion-types/generic-select-promotion-types.component';
import { ActionsV2__communicationType } from '../../../store/actionsV2-FormStateModel';
import { PagedList } from 'core/models/paged-list.model';
@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private configBase = _get(environment, 'apiBaseUrl_promotionV2');
  private configBaseTags = _get(environment, 'apiBaseUrl_adaptations');
  private configurationConfig = environment.promotionV2.configuration.api;
  private actionConfig = environment.promotionV2.action.api;

  constructor(private http: HttpClient, private messageStructuresService: MessageStructuresListService) {}

  getItems(body: any): Observable<CommunicationItem[]> {
    const useDummyData = false;
    if (useDummyData) return of(itemsDummyData);
    const _url = `${this.configBaseTags}${this.configurationConfig.resources.getItems}`;
    return this.http.post<CommunicationItem[]>(_url, body).pipe(
      tap(data => _logTap(`${CommunicationService.name}::getItems (tap)\n\tdata: %o`, data)),
      kayshOperator('getItems', { body }, { localStorage: false, maxTime: _ms('30m') })
    );
  }

  getCommunicationCombosOptions(payload: PostPreviewPromotionalMessageDTO): Observable<PromotionalMessagePreviewResponseDTO> {
    const useDummyData = false;
    if (useDummyData) return of(communicationCombosOptions_dummy);

    const _payloadForKaysh = { ...payload, promotionCombos: payload.promotionCombos.map(({ value, ...rest }) => ({ ...rest })) };

    const transformedCombos = _mapCombosToFlat(payload.promotionCombos);
    const newPayload: PostPreviewPromotionalMessageDTO = {
      ...payload,
      promotionCombos: transformedCombos,
    };

    const _url = `${this.configBase}${this.actionConfig.resources.getCommunicationCombosOptions}`;
    return this.http.post<PromotionalMessagePreviewResponseDTO>(_url, newPayload).pipe(
      tap(data => _logTap(`${CommunicationService.name}::getCommunicationCombosOptions (tap)\n\tdata: %o`, data)),
      kayshOperator('getCommunicationCombosOptions', { _payloadForKaysh }, { localStorage: false, maxTime: _ms('30m') })
    );
  }

  getStructures(
    promotionType: PromotionTypes,
    communicationType: ActionsV2__communicationType,
    promotionProcessId: number
  ): Observable<PagedList<MessageStructuresModel>> {
    const filters: MessageStructuresListFiltersModel = {
      onlyInactives: false,
      promotionType: promotionType,
      promotionProcessId: promotionProcessId,
      communicationType: communicationType,
      query: '',
      pageIndex: 0,
      pageSize: 1000,
    };
    return this.messageStructuresService.getPromotionalMessageList(filters, true);
  }
}

export const _mapCombosToFlat = (combos: PromotionCombo[]): PromotionCombo[] => {
  return combos.flatMap(combo => {
    const { childrenCombos, ...rest } = combo;
    if (combo.staggered && childrenCombos?.length > 0) {
      return [
        { ...rest, value: combo.value || 0 },
        ...childrenCombos.map(child => {
          const newBlocks = child.childrenBlocks.map(childBlock => {
            const parentBlock = combo.block.find(b => b.blockId === childBlock.blockId);
            return {
              ...parentBlock,
              messageCommunicationType: childBlock.messageCommunicationType,
              stock: childBlock.stock,
            };
          });

          const newCombo: PromotionCombo = {
            id: child.comboId,
            staggered: true,
            block: newBlocks,
            order: child.order,
            value: child.value || 0,
          };
          return newCombo;
        }),
      ];
    }
    return [{ ...combo, value: combo.value || 0 }];
  });
};

export const _mapFlatToStaggeredCombos = (combos: PromotionCombo[]): PromotionCombo[] => {
  // Separar combos escalonados y no escalonados
  const staggeredCombos = combos.filter(c => c.staggered);
  const nonStaggeredCombos = combos.filter(c => !c.staggered);

  if (staggeredCombos.length === 0) {
    // No hay combos escalonados, devolver tal cual
    return combos;
  }
  // El combo padre es el que tiene el menor order (o el primero)
  const parentCombo = staggeredCombos.reduce((prev, curr) => ((prev.order ?? 0) <= (curr.order ?? 0) ? prev : curr));
  // Los hijos son los demás combos escalonados
  const childrenCombos = staggeredCombos
    .filter(c => c.order !== parentCombo.order)
    .map(child => ({
      comboId: child.id,
      parentId: parentCombo.id,
      value: child.value,
      order: child.order,
      childrenBlocks: child.block.map(b => ({
        blockId: b.blockId,
        stock: b.stock,
        name: b.name,
        messageCommunicationType: b.messageCommunicationType,
        order: b.order,
      })),
    }));
  // Reconstruir el combo escalonado
  const reconstructedStaggered: PromotionCombo = {
    ...parentCombo,
    childrenCombos,
  };
  // Devolver el combo escalonado y los no escalonados
  return [reconstructedStaggered, ...nonStaggeredCombos];
};
